import React, { useEffect, useMemo, useState } from 'react';
import { registerStudent, loginStudent } from '../services/authService';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/RegisterPage.css';

export default function RegisterPage() {
  const navigate = useNavigate();

  // --- UI State ---
  const [step, setStep] = useState(1);
  const totalSteps = 5;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Data State ---
  const [cities, setCities] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [customUniversity, setCustomUniversity] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [customProfession, setCustomProfession] = useState('');

  // KVKK
  const [kvkkText, setKvkkText] = useState('');
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [showKvkkModal, setShowKvkkModal] = useState(false);

  // Alerts
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // --- Form State ---
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    highSchoolStatus: '',            // "Mezun" | "Öğrenci"
    highSchoolGraduateYear: '',
    bio: '',
    profession: '',
    phoneNumber: '',
    city: '',
    universityName: '',
    username: '',
    password: '',
  });

  // ---- Fetch data ----
  useEffect(() => {
    fetch('https://turkiyeapi.dev/api/v1/provinces')
      .then(res => res.json())
      .then(data => {
        if (data.status === 'OK') setCities(data.data.map(c => c.name));
      })
      .catch(err => console.error('İl API hatası:', err));
  }, []);

  useEffect(() => {
    fetch('/data/meslekler.json')
      .then(res => res.json())
      .then(setProfessions)
      .catch(err => console.error('Meslek API hatası:', err));
  }, []);

  useEffect(() => {
    fetch('/data/uni.json')
      .then(res => res.json())
      .then(setUniversities)
      .catch(err => console.error('Üniversite verisi alınamadı:', err));
  }, []);

  useEffect(() => {
    fetch('/data/kvkk.json')
      .then(res => res.json())
      .then(data => {
        if (data.pages && Array.isArray(data.pages)) {
          setKvkkText(data.pages.map(p => p.content).join('\n\n'));
        } else {
          setKvkkText(data.text || '');
        }
      })
      .catch(err => console.error('KVKK metni yüklenemedi:', err));
  }, []);

  // --- Helpers ---
 // --- Helpers ---
const currentYear = useMemo(() => new Date().getFullYear(), []);

const graduateYearStudent = useMemo(() => {
  if (formData.highSchoolStatus === 'Öğrenci') {
    // currentYear → currentYear+5
    return Array.from({ length: 6 }, (_, i) => currentYear + i);
  } else {
    // Mezun (1923 → currentYear)
    return Array.from({ length: currentYear - 1923 + 1 }, (_, i) => currentYear - i);
  }
}, [currentYear, formData.highSchoolStatus]);


  const handleChange = (e) => {
    const { name, value, type } = e.target;

    // radio/checkbox dışındaki durumlar
    if (name === 'universityName' && value === 'Diğer') setCustomUniversity('');
    if (name === 'city' && value === 'Diğer') setCustomCity('');
    if (name === 'profession' && value === 'Diğer') setCustomProfession('');

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const nextStep = () => {
    if (!validateStep(step)) return;
    setErrorMessage('');
    setStep(s => Math.min(totalSteps, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setErrorMessage('');
    setStep(s => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- Per-step validation (lightweight) ---
 const validateStep = (s) => {
  setErrorMessage('');

  if (s === 1) {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setErrorMessage('Ad ve Soyad zorunludur.');
      return false;
    }
    if (!formData.email.trim()) {
      setErrorMessage('E-posta zorunludur.');
      return false;
    }
    const emailOk = /\S+@\S+\.\S+/.test(formData.email);
    if (!emailOk) {
      setErrorMessage('Lütfen geçerli bir e-posta giriniz.');
      return false;
    }
  }

  if (s === 2) {
    if (!formData.highSchoolStatus) {
      setErrorMessage('Lise durumunuzu seçiniz.');
      return false;
    }
    if (!formData.highSchoolGraduateYear) {
      setErrorMessage('Lise mezuniyet yılını seçiniz.');
      return false;
    }

    const currentYear = new Date().getFullYear();
    const gradYear = parseInt(formData.highSchoolGraduateYear);

    if (formData.highSchoolStatus === 'Öğrenci' && gradYear < currentYear) {
      setErrorMessage('Lütfen mezun olacağınız yılı doğru seçiniz.');
      return false;
    }

    if (formData.highSchoolStatus === 'Mezun' && gradYear > currentYear) {
      setErrorMessage('Lütfen mezuniyet yılınızı doğru seçiniz.');
      return false;
    }

    if (formData.universityName === 'Diğer' && !customUniversity.trim()) {
      setErrorMessage('Lütfen üniversite adınızı giriniz.');
      return false;
    }
  }


    if (s === 3) {
      if (formData.city === 'Diğer' && !customCity.trim()) {
        setErrorMessage('Lütfen şehir adınızı giriniz.');
        return false;
      }
      if (formData.profession === 'Diğer' && !customProfession.trim()) {
        setErrorMessage('Lütfen meslek adınızı giriniz.');
        return false;
      }
      // Telefon boş olabilir (opsiyonel), ancak doluysa basit kontrol:
      if (formData.phoneNumber && !/^\d{10,11}$/.test(formData.phoneNumber.replace(/\s+/g, ''))) {
        setErrorMessage('Telefon numarasını boş bırakın ya da 10-11 haneli girin.');
        return false;
      }
    }

    if (s === 4) {
      if (!kvkkAccepted) {
        setErrorMessage('Kayıt için KVKK metnini kabul etmelisiniz.');
        return false;
      }
    }

    if (s === 5) {
      const usernameRegex = /^[a-zA-Z0-9_]+$/;
      if (!formData.username.trim() || !usernameRegex.test(formData.username)) {
        setErrorMessage('Kullanıcı adı: sadece harf, rakam ve "_" içerebilir.');
        return false;
      }
      if (!formData.password || formData.password.length < 4) {
        setErrorMessage('Şifre en az 4 karakter olmalıdır.');
        return false;
      }
    }

    return true;
  };

  // --- Submit (same logic, improved UX) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(5)) return;

    const finalFormData = {
      ...formData,
      city: formData.city === 'Diğer' ? customCity : formData.city,
      profession: formData.profession === 'Diğer' ? customProfession : formData.profession,
      universityName: formData.universityName === 'Diğer' ? customUniversity : formData.universityName,
      highSchoolGraduateYear: parseInt(formData.highSchoolGraduateYear) || 0,
    };

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const resp = await registerStudent(finalFormData);
      setSuccessMessage(resp.responseMessage || 'Kayıt başarılı! Giriş yapılıyor...');
    } catch (err) {
      let message = err.message || 'Kayıt başarısız oldu.';
      if (message.toLowerCase().includes('timeout') || message.toLowerCase().includes('expired')) {
        message = 'Veritabanı yükleniyor, lütfen bekleyin. Bu işlem 30 saniye kadar sürebilir.';
      }
      setErrorMessage(message);
      setIsSubmitting(false);
      return;
    }

    // Auto login
    try {
      const loginData = { Username: finalFormData.username, Password: finalFormData.password };
      const response = await loginStudent(loginData);
      if (response.isSuccess && response.data) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('username', response.data.username);
        localStorage.setItem('studentId', response.data.id);
        localStorage.setItem('isAdmin', response.data.isAdmin);
        setSuccessMessage('Kayıt ve giriş başarılı! Yönlendiriliyorsunuz...');
        setTimeout(() => navigate('/home'), 800);
      } else {
        setErrorMessage('Giriş yapılamadı.');
      }
    } catch (err) {
      setErrorMessage(err.message || 'Giriş sırasında hata oluştu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Step header / progress ---
  const progressPercent = Math.round((step / totalSteps) * 100);

  return (
    <>
      <Navbar />

      <div className="register-page">
        <div className="form-container">
          <h2>Kayıt Ol</h2>

          <div className="progress-wrap" aria-label={`İlerleme: %${progressPercent}`}>
            <div className="progress-bar" style={{ width: `${progressPercent}%` }} />
            <div className="progress-steps">
              {[1, 2, 3, 4, 5].map(n => (
                <div key={n} className={`dot ${n <= step ? 'active' : ''}`} aria-hidden="true" />
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* STEP 1 */}
            {step === 1 && (
              <div className="form-step">
                <div className="grid">
                  <input name="firstName" placeholder="Ad *" value={formData.firstName} onChange={handleChange} required />
                  <input name="lastName" placeholder="Soyad *" value={formData.lastName} onChange={handleChange} required />
                  <input name="email" type="email" placeholder="E-posta *" value={formData.email} onChange={handleChange} required />
                </div>
                <div className="actions">
                  <button type="button" className="submit-button" onClick={nextStep}>İleri</button>
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div className="form-step">
                <p className="label">Liseye Devam Durumunuz *</p>
                <div className="radio-group">
                  <label>
                    <input type="radio" name="highSchoolStatus" value="Mezun" checked={formData.highSchoolStatus === 'Mezun'} onChange={handleChange} required />
                    Mezun
                  </label>
                  <label>
                    <input type="radio" name="highSchoolStatus" value="Öğrenci" checked={formData.highSchoolStatus === 'Öğrenci'} onChange={handleChange} required />
                    Öğrenci
                  </label>
                </div>

               <select name="highSchoolGraduateYear" value={formData.highSchoolGraduateYear} onChange={handleChange} required>
                <option value="">Lise Mezuniyet Yılı</option>
                {graduateYearStudent.map(y => <option key={y} value={y}>{y}</option>)}
              </select>


                {formData.highSchoolStatus === 'Mezun' && (
                  <>
                    <select name="universityName" value={formData.universityName} onChange={handleChange}>
                      <option value="">Üniversite Seçin (Opsiyonel)</option>
                      {universities.map((u, i) => <option key={i} value={u}>{u}</option>)}
                      <option value="Diğer">Diğer</option>
                    </select>
                    {formData.universityName === 'Diğer' && (
                      <input placeholder="Üniversite Adı" value={customUniversity} onChange={(e) => setCustomUniversity(e.target.value)} />
                    )}
                  </>
                )}

                <div className="actions">
                  <button type="button" className="submit-button ghost" onClick={prevStep}>Geri</button>
                  <button type="button" className="submit-button" onClick={nextStep}>İleri</button>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <div className="form-step">
                <select name="profession" value={formData.profession} onChange={handleChange}>
                  <option value="">Meslek Seçiniz (Opsiyonel)</option>
                  {professions.map(p => <option key={p} value={p}>{p}</option>)}
                  <option value="Diğer">Diğer</option>
                </select>
                {formData.profession === 'Diğer' && (
                  <input placeholder="Meslek Adı" value={customProfession} onChange={(e) => setCustomProfession(e.target.value)} />
                )}

                <select name="city" value={formData.city} onChange={handleChange}>
                  <option value="">Yaşadığınız İl (Opsiyonel)</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="Diğer">Diğer</option>
                </select>
                {formData.city === 'Diğer' && (
                  <input placeholder="Şehir Adı" value={customCity} onChange={(e) => setCustomCity(e.target.value)} />
                )}

                <input name="phoneNumber" placeholder="Örn: 5xx xxx xxxx (Opsiyonel)" value={formData.phoneNumber} onChange={handleChange} />

                <div className="actions">
                  <button type="button" className="submit-button ghost" onClick={prevStep}>Geri</button>
                  <button type="button" className="submit-button" onClick={nextStep}>İleri</button>
                </div>
              </div>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <div className="form-step">
                <textarea name="bio" placeholder="Kendinizi tanıtın... (Opsiyonel)" value={formData.bio} onChange={handleChange} rows={5} />

                <label className="kvkk-label">
                  <input type="checkbox" checked={kvkkAccepted} onChange={(e) => setKvkkAccepted(e.target.checked)} />
                  KVKK Aydınlatma Metnini okudum ve kabul ediyorum.
                </label>

                <div className="actions">
                  <button type="button" className="submit-button ghost" onClick={prevStep}>Geri</button>
                  <button type="button" className="submit-button" onClick={nextStep}>İleri</button>
                </div>
              </div>
            )}

            {/* STEP 5 */}
            {step === 5 && (
              <div className="form-step">
                <input name="username" placeholder="Kullanıcı Adı *" value={formData.username} onChange={handleChange} required />
                <input name="password" type="password" placeholder="Şifre *" value={formData.password} onChange={handleChange} required />

                <div className="actions">
                  <button type="button" className="submit-button ghost" onClick={prevStep}>Geri</button>
                  <button type="submit" className="submit-button">{isSubmitting ? 'Gönderiliyor...' : 'Kaydı Tamamla'}</button>
                </div>
              </div>
            )}
          </form>

          {errorMessage && <p className="error-message">{errorMessage}</p>}
          {successMessage && <p className="success-message">{successMessage}</p>}

          <p className="login-link">
            Hesabın var mı? <Link to="/">Giriş Yap</Link>
          </p>
        </div>
      </div>

      <Footer />
    </>
  );
}